import { useState } from "react";
import axiosClient from "../axiosClient";
import "./middleSchoolPrep.css";
import wechatQr from "./images/svt-wechat-support.jpg";

const GRADE_OPTIONS = ["4th grade", "5th grade"];

const TIME_SLOT_OPTIONS = [
  "Weekday morning (9:00 AM – 12:00 PM)",
  "Weekday afternoon (12:00 PM – 5:00 PM)",
  "Weekday evening (5:00 PM – 8:00 PM)",
  "Weekend morning",
  "Weekend afternoon",
];

const ENGLISH_DOCUMENTS = [
  {
    label: "Admission Brochure",
    file: "SVT MS Prep AdmissionBrochure_0917.pdf",
  },
  {
    label: "Weekly Schedule",
    file: "SVT MS Prep Wkly Schedule.pdf",
  },
  {
    label: "ELA Reading & Writing Syllabus (16 weeks)",
    file: "SVT MSprep ELA  Reading&Writing_16Wk Syllabus.pdf",
  },
  {
    label: "Pre-Algebra / IMT1 Syllabus (16 weeks)",
    file: "SVT MSprep PreAlgebra_IMT1_16Wk_Syllabus.pdf",
  },
  {
    label: "Science Syllabus (16 weeks)",
    file: "SVT_Science_16Week_Syllabus_EN.pdf",
  },
];

const CHINESE_DOCUMENTS = [
  {
    label: "招生简章",
    file: "SVT初中预备班招生简章_0917.pdf",
  },
  {
    label: "每周课程安排",
    file: "SVT初中预备班每周课程安排.pdf",
  },
  {
    label: "阅读与理解大纲",
    file: "SVT初中预备班阅读&理解大纲.pdf",
  },
  {
    label: "数学 PreAlgebra / IMT1 大纲",
    file: "SVT初中预备班数学 PreAlgebra_IMT1大纲.pdf",
  },
  {
    label: "科学课程设置（16周）",
    file: "SVT16周科学课程设置.pdf",
  },
];

const emailPattern = /^\S+@\S+\.\S+$/;
const phonePattern = /^[\d\s()+.-]{7,}$/;

const MiddleSchoolPrep = () => {
  const [contactMethod, setContactMethod] = useState("phone");
  const [formData, setFormData] = useState({
    contactValue: "",
    studentGrade: "",
    preferredTimeSlot: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);

  const validateField = (name, value, method = contactMethod) => {
    switch (name) {
      case "contactValue":
        if (!value.trim()) {
          return method === "phone"
            ? "Phone number is required"
            : "Email address is required";
        }
        if (method === "email") {
          return emailPattern.test(value) ? "" : "Invalid email address";
        }
        return phonePattern.test(value) ? "" : "Invalid phone number";
      case "studentGrade":
        return value.trim() ? "" : "Student grade is required";
      case "preferredTimeSlot":
        return value.trim() ? "" : "Preferred contact time is required";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactMethodChange = (method) => {
    setContactMethod(method);
    setErrors((prev) => ({
      ...prev,
      contactValue: validateField("contactValue", formData.contactValue, method),
    }));
  };

  const validateForm = () => {
    const newErrors = {
      contactValue: validateField("contactValue", formData.contactValue),
      studentGrade: validateField("studentGrade", formData.studentGrade),
      preferredTimeSlot: validateField(
        "preferredTimeSlot",
        formData.preferredTimeSlot
      ),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axiosClient.post("/middleSchoolPrepLeads", {
        contactMethod,
        contactValue: formData.contactValue.trim(),
        studentGrade: formData.studentGrade,
        preferredTimeSlot: formData.preferredTimeSlot,
      });

      setDialogMessage(
        response.data.message ||
          "Thank you. We have received your request and will contact you soon."
      );
      setShowDialog(true);
      setFormData({
        contactValue: "",
        studentGrade: "",
        preferredTimeSlot: "",
      });
      setErrors({});
      setContactMethod("phone");
    } catch (error) {
      const response = error.response;
      const validationErrors = response?.data?.errors;

      if (validationErrors) {
        const nextErrors = {};
        Object.keys(validationErrors).forEach((key) => {
          nextErrors[key] = validationErrors[key][0];
        });
        setErrors(nextErrors);
      }

      setDialogMessage(
        response?.data?.message ||
          "We could not submit your request. Please try again later."
      );
      setShowDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    !formData.contactValue ||
    !formData.studentGrade ||
    !formData.preferredTimeSlot ||
    Object.values(errors).some((error) => error) ||
    isSubmitting;

  return (
    <div className="middle-school-prep-page">
      <div className="middle-school-prep-container">
        <div className="middle-school-prep-main">
          <div className="middle-school-prep-intro">
            <div className="middle-school-prep-intro-col middle-school-prep-intro-en">
              <p>
                <strong>SVT Middle School Prep Program</strong>
              </p>
              <p>
                This program is designed for students entering or currently in
                grades 4 and 5, aiming to help them build a solid foundation and
                ensure a smooth transition to middle school. The academic
                standards align with the Bellevue School District&apos;s
                curriculum requirements for grades 4 and 5 while effectively
                bridging the gap to middle school coursework. The core curriculum
                includes English Language Arts (ELA, covering reading and
                writing), Pre-Algebra/Math, Science, a baking workshop, hands-on
                projects, &quot;Homework Studio&quot; (providing homework
                assistance), and support for personal development and planning.
              </p>
              <p>
                Academic courses follow a unified design framework:
              </p>
              <ul>
                <li>
                  <strong>Mathematics:</strong> SVT Middle School Prep Course
                  (aligned with district 5th-grade standards and bridging to the
                  IMT1 course)
                </li>
                <li>
                  <strong>English (ELA):</strong> Focused on the core areas of
                  reading and writing
                </li>
                <li>
                  <strong>Science:</strong> Combines introductory middle school
                  science content with a review of key 5th-grade concepts
                </li>
              </ul>
              <p>
                Value-added components include weekly or monthly communication
                regarding student social-emotional development and monthly
                consultations with parents on academic progression and planning.
              </p>
              <p>
                The weekly schedule runs from 2:50 PM to 6:00 PM on Mondays,
                Tuesdays, Thursdays, and Fridays, and from 12:35 PM to 6:00 PM on
                Wednesdays. The schedule comprises 75 minutes of academic
                instruction, a 45-minute homework assistance session, and a
                15-minute break; Wednesdays feature a baking workshop and other
                hands-on projects. Students may choose between two enrollment
                options: a full-week (5-day) program or a 3-day program. Early
                pickup is permitted.
              </p>
            </div>
            <div className="middle-school-prep-intro-col middle-school-prep-intro-zh">
              <p>
                <strong>SVT 初中预备班项目</strong>
              </p>
              <p>
                本项目主要面向即将升入或就读于四年级和五年级的学生，旨在帮助他们打下坚实基础并顺利过渡到初中阶段。学术标准既符合贝尔维尤学区（Bellevue
                School
                District）四年级和五年级的教学要求，又能有效衔接初中课程。核心课程包括：英语语言艺术（ELA，涵盖阅读与写作）、代数预科/数学、科学、烘焙工作坊、动手实践项目、“作业辅导室”（Homework
                Studio，提供作业辅导），以及个人发展与规划支持。
              </p>
              <p>学术课程遵循统一的设计框架：</p>
              <ul>
                <li>
                  <strong>数学：</strong>
                  SVT初中预备课程（对标学区五年级标准，并衔接IMT1课程）
                </li>
                <li>
                  <strong>英语（ELA）：</strong>
                  以阅读和写作两大核心板块为重点
                </li>
                <li>
                  <strong>科学：</strong>
                  结合初中科学入门内容与五年级关键概念复习
                </li>
              </ul>
              <p>
                增值项目包括：每周／月学生心智发展沟通、每月面向家长的升学规划咨询。
              </p>
              <p>
                每周日程安排为：周一、周二、周四和周五下午2:50至6:00，周三下午12:35至6:00。日程包含75分钟的学术课程、45分钟的作业辅导时段和15分钟的休息时间；周三特别安排了烘焙工作坊及其他动手实践项目。学生可选择两种注册方案：全周（5天）课程或3天课程。允许提前接回学生。
              </p>
            </div>
          </div>

          <div className="middle-school-prep-documents">
            <div className="middle-school-prep-documents-col">
              <h3>English Documents</h3>
              <ul>
                {ENGLISH_DOCUMENTS.map((doc) => (
                  <li key={doc.file}>
                    Download{" "}
                    <a
                      href={`./${encodeURIComponent(doc.file).replace(/%2F/g, "/")}`}
                      target="msprepdocs"
                      rel="noopener noreferrer"
                    >
                      {doc.label}
                    </a>{" "}
                    in PDF format.
                  </li>
                ))}
              </ul>
            </div>
            <div className="middle-school-prep-documents-col">
              <h3>中文资料</h3>
              <ul>
                {CHINESE_DOCUMENTS.map((doc) => (
                  <li key={doc.file}>
                    下载{" "}
                    <a
                      href={`./${encodeURIComponent(doc.file).replace(/%2F/g, "/")}`}
                      target="msprepdocs"
                      rel="noopener noreferrer"
                    >
                      {doc.label}
                    </a>{" "}
                    （PDF）
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="registeration-form-container">
            <h3>
              <b>Request a Follow-Up</b>
            </h3>
            <h3>
              Tell us how and when to contact you. We will follow up to share
              program details.
            </h3>
            <h3>(* - Required Information)</h3>

            <div className="middle-school-prep-layout">
              <form className="registeration-form" onSubmit={handleSubmit}>
                <fieldset className="middle-school-prep-fieldset">
                  <legend>Preferred contact method *</legend>
                  <div className="middle-school-prep-radio-row">
                    <label className="middle-school-prep-radio">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={contactMethod === "phone"}
                        onChange={(e) =>
                          handleContactMethodChange(e.target.value)
                        }
                      />
                      Phone
                    </label>
                    <label className="middle-school-prep-radio">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={contactMethod === "email"}
                        onChange={(e) =>
                          handleContactMethodChange(e.target.value)
                        }
                      />
                      Email
                    </label>
                  </div>
                </fieldset>

                <label htmlFor="contactValue">
                  {contactMethod === "phone"
                    ? "Phone number *"
                    : "Email address *"}
                </label>
                <input
                  type={contactMethod === "phone" ? "tel" : "email"}
                  id="contactValue"
                  name="contactValue"
                  value={formData.contactValue}
                  onChange={handleChange}
                  placeholder={
                    contactMethod === "phone"
                      ? "e.g. (425) 555-0123"
                      : "e.g. name@example.com"
                  }
                />
                {errors.contactValue && (
                  <span className="registration-form-error">
                    {errors.contactValue}
                  </span>
                )}

                <label htmlFor="studentGrade">Student grade *</label>
                <select
                  id="studentGrade"
                  name="studentGrade"
                  value={formData.studentGrade}
                  onChange={handleChange}
                >
                  <option value="">Select grade</option>
                  {GRADE_OPTIONS.map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </select>
                {errors.studentGrade && (
                  <span className="registration-form-error">
                    {errors.studentGrade}
                  </span>
                )}

                <label htmlFor="preferredTimeSlot">
                  Preferred contact time *
                </label>
                <select
                  id="preferredTimeSlot"
                  name="preferredTimeSlot"
                  value={formData.preferredTimeSlot}
                  onChange={handleChange}
                >
                  <option value="">Select a time slot</option>
                  {TIME_SLOT_OPTIONS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                {errors.preferredTimeSlot && (
                  <span className="registration-form-error">
                    {errors.preferredTimeSlot}
                  </span>
                )}

                <div className="registration-form-actions">
                  <input
                    type="submit"
                    value={isSubmitting ? "Submitting..." : "Request Contact"}
                    disabled={isSubmitDisabled}
                  />
                </div>
              </form>

              <aside className="middle-school-prep-contact-aside">
                <h3>Prefer to reach us directly?</h3>
                <p>
                  You can also call or email our school or scan the QR code below
                  to add SVT Support on WeChat.
                </p>
                <p className="middle-school-prep-phone">
                  Call us:{" "}
                  <a href="tel:+14254571965">(+1) 425-457-1965</a>
                </p>
                <p className="middle-school-prep-email">
                  Email:{" "}
                  <a href="mailto:contact@seattlevt.org">
                    contact@seattlevt.org
                  </a>
                </p>
                <img
                  src={wechatQr}
                  alt="Scan QR code to add SVT Support on WeChat"
                  className="middle-school-prep-qr"
                />
                <p className="middle-school-prep-qr-caption">
                  Scan to add SVT Support on WeChat
                </p>
              </aside>
            </div>
          </div>
        </div>
      </div>

      {showDialog && (
        <div className="middle-school-prep-dialog-overlay">
          <div className="middle-school-prep-success-dialog">
            <p>{dialogMessage}</p>
            <button
              type="button"
              className="middle-school-prep-success-close"
              onClick={() => setShowDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiddleSchoolPrep;
